import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { LogicButton } from '@/components/buttons/LogicButton';
import { FieldError } from '@/components/Form/fields';
import { Text } from '@/components/typography';

import RuleBuilderConditionGroup from './RuleBuilderConditionGroup';
import RuleBuilderDateRange from './RuleBuilderDateRange';
import RuleEntityDropdown from './RuleEntityDropdown';
import type { OrConditionPath, RuleEntityType } from './types';
import { useAudienceRuleForm } from './types';
import useRuleEntities from './useRuleEntities';
import { initializeCondition } from './util';

const MAX_OR_CONDITIONS = 3;

type Props = {
  disabled?: boolean;
};

const buildOrConditionPath = (index: number): OrConditionPath => `rules.orConditions[${index}]`;

const RuleBuilder = ({ disabled = false }: Props) => {
  const form = useAudienceRuleForm();
  const { ruleEntities } = useRuleEntities();

  const orConditionsSchema = z
    .array(z.any())
    .min(1, t`Please add at least one rule`)
    .max(MAX_OR_CONDITIONS, t`You can only add up to ${MAX_OR_CONDITIONS} OR groups`);

  return (
    <div
      className="flex min-h-125 flex-col items-start justify-start gap-6 pb-24"
      data-testid="rule-builder"
    >
      <div className="flex items-center gap-4">
        <Text variant="body1">
          <Trans>Audience will include Companies with the following criteria in the:</Trans>
        </Text>
        <form.AppField name="rules.dateRange">
          {(field) => (
            <RuleBuilderDateRange
              value={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              disabled={disabled}
            />
          )}
        </form.AppField>
      </div>

      <form.Field
        name="rules.orConditions"
        mode="array"
        validators={{
          onDynamic: orConditionsSchema,
        }}
      >
        {(orField) => {
          const conditions = orField.state.value ?? [];
          return (
            <>
              {conditions.map((_, index: number) => {
                const orConditionPath = buildOrConditionPath(index);

                return (
                  <div
                    key={index}
                    className="flex w-full flex-col items-start justify-center gap-4"
                  >
                    {index > 0 && <LogicButton value="OR" isReadOnly />}
                    <RuleBuilderConditionGroup
                      form={form}
                      fields={orConditionPath}
                      basePath={orConditionPath}
                      ruleEntities={ruleEntities}
                      disabled={disabled}
                      onRemoveOrCondition={() => orField.removeValue(index)}
                    />
                  </div>
                );
              })}
              {orField.state.meta.errors.length > 0 && (
                <FieldError errors={orField.state.meta.errors} />
              )}
              <RuleEntityDropdown
                type={conditions.length === 0 ? 'ADD' : 'OR'}
                ruleEntities={ruleEntities}
                disabled={conditions.length >= MAX_OR_CONDITIONS || disabled}
                title={
                  conditions.length >= MAX_OR_CONDITIONS
                    ? t`You can only add up to ${MAX_OR_CONDITIONS} OR groups`
                    : null
                }
                onSelect={(ruleEntity: RuleEntityType, attribute) =>
                  orField.pushValue({
                    andConditions: [initializeCondition(ruleEntity, attribute)],
                  })
                }
              />
            </>
          );
        }}
      </form.Field>
    </div>
  );
};

export default RuleBuilder;
